import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

const SPEED_OPTIONS = [
  { value: 'static', label: 'Static' },
  { value: 'slow',   label: 'Slow'   },
  { value: 'normal', label: 'Normal' },
  { value: 'fast',   label: 'Fast'   },
];

// ─── Toggle switch ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative inline-flex items-center flex-shrink-0 cursor-pointer transition-colors duration-300 focus:outline-none"
      style={{
        width: 52,
        height: 28,
        borderRadius: 14,
        backgroundColor: checked ? '#F7941D' : 'rgba(255,255,255,0.12)',
      }}
    >
      <span
        className="inline-block rounded-full bg-white shadow-md transition-transform duration-300"
        style={{
          width: 22,
          height: 22,
          transform: checked ? 'translateX(26px)' : 'translateX(3px)',
        }}
      />
    </button>
  );
}

// ─── Main section ──────────────────────────────────────────────────────────────

function AnnouncementSection() {
  const [recordId, setRecordId]   = useState(null);
  const [message, setMessage]     = useState('');
  const [url, setUrl]             = useState('');
  const [visible, setVisible]     = useState(true);
  const [speed, setSpeed]         = useState('normal');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error'

  // ── Fetch on mount ───────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchAnnouncement() {
      setLoading(true);
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setRecordId(data.id);
        setMessage(data.message ?? '');
        setUrl(data.url ?? '');
        setVisible(data.visible ?? true);
        setSpeed(data.speed ?? 'normal');
      }
      setLoading(false);
    }
    fetchAnnouncement();
  }, []);

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);

    const payload = { message, url: url.trim() || null, visible, speed };
    let error;

    if (recordId) {
      ({ error } = await supabase
        .from('announcements')
        .update(payload)
        .eq('id', recordId));
    } else {
      const { data, error: insertError } = await supabase
        .from('announcements')
        .insert(payload)
        .select()
        .single();
      error = insertError;
      if (!insertError && data) setRecordId(data.id);
    }

    setSaving(false);
    setSaveStatus(error ? 'error' : 'success');

    if (!error) {
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div>
          <div className="h-7 w-48 rounded-lg bg-white/10 mb-2" />
          <div className="h-4 w-72 rounded bg-white/5" />
        </div>
        <div className="h-40 rounded-xl bg-white/5" />
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-8 max-w-2xl">

      {/* Header */}
      <div>
        <h1 className="font-poppins font-bold text-white text-2xl">Announcement</h1>
        <p className="font-poppins text-white/40 text-sm mt-1">
          This is the scrolling banner that appears at the top of your website.
        </p>
      </div>

      {/* Card */}
      <div
        className="rounded-2xl flex flex-col gap-7 p-7"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
      >

        {/* Message field */}
        <div className="flex flex-col gap-2">
          <label className="font-poppins font-semibold text-white text-sm">
            Announcement Message
          </label>
          <p className="font-poppins text-white/40 text-xs -mt-1">
            What do you want visitors to see in the scrolling banner?
          </p>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
            placeholder="e.g. Trivia Night every Thursday at The Crown — doors open at 7pm!"
            className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 resize-none focus:outline-none transition-colors duration-200"
            style={{
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(255,255,255,0.1)',
              lineHeight: 1.6,
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          />
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />

        {/* Link URL field */}
        <div className="flex flex-col gap-2">
          <label className="font-poppins font-semibold text-white text-sm">
            Link URL <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>(optional)</span>
          </label>
          <p className="font-poppins text-white/40 text-xs -mt-1">
            If set, the entire banner becomes a clickable link.
          </p>
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
            style={{
              backgroundColor: '#2A2A2A',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          />
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />

        {/* Visible toggle */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-poppins font-semibold text-white text-sm">Show on site</p>
            <p className="font-poppins text-white/40 text-xs mt-0.5">
              Turn this off to hide the banner without deleting your message.
            </p>
          </div>
          <Toggle checked={visible} onChange={setVisible} />
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />

        {/* Scroll speed */}
        <div className="flex flex-col gap-3">
          <div>
            <p className="font-poppins font-semibold text-white text-sm">Scroll Speed</p>
            <p className="font-poppins text-white/40 text-xs mt-0.5">
              How fast should the text move across the screen?
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {SPEED_OPTIONS.map(opt => {
              const isSelected = speed === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSpeed(opt.value)}
                  className="font-poppins text-sm font-medium px-5 py-2 rounded-lg transition-all duration-200 focus:outline-none"
                  style={{
                    backgroundColor: isSelected ? '#F7941D' : 'rgba(255,255,255,0.06)',
                    color: isSelected ? '#1A1A1A' : 'rgba(255,255,255,0.6)',
                    border: isSelected ? '1px solid #F7941D' : '1px solid rgba(255,255,255,0.1)',
                    fontWeight: isSelected ? 700 : 500,
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                    }
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {/* Save row */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="font-poppins font-bold text-sm px-8 py-3 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: saving ? 'rgba(247,148,29,0.4)' : '#F7941D',
            color: '#1A1A1A',
            cursor: saving ? 'not-allowed' : 'pointer',
            minWidth: 120,
          }}
          onMouseEnter={e => {
            if (!saving) e.currentTarget.style.backgroundColor = '#ffb83f';
          }}
          onMouseLeave={e => {
            if (!saving) e.currentTarget.style.backgroundColor = '#F7941D';
          }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>

        {/* Status message */}
        {saveStatus === 'success' && (
          <div className="flex items-center gap-2">
            <span style={{ color: '#4ade80', fontSize: 16 }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>
              Saved successfully!
            </p>
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="flex items-center gap-2">
            <span style={{ color: '#f87171', fontSize: 16 }}>✕</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
              Something went wrong. Please try again.
            </p>
          </div>
        )}
      </div>

    </div>
  );
}

export default AnnouncementSection;
